import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  FileText, 
  Clock, 
  TrendingUp, 
  TrendingDown,
  Minus,
  CheckCircle,
  AlertCircle,
  Loader2,
  Timer
} from "lucide-react";
import { differenceInDays, differenceInHours, format } from "date-fns";

interface ClaimsProcessingStats {
  avgProcessingDays: number;
  avgToInReview: number;
  avgToApproval: number;
  totalProcessed: number;
  pendingCount: number;
  inReviewCount: number;
  approvedCount: number;
  deniedCount: number;
  oldestPendingDays: number;
  processedThisMonth: number;
  processedLastMonth: number;
}

export function ClaimsProcessingAnalytics() {
  const [stats, setStats] = useState<ClaimsProcessingStats>({
    avgProcessingDays: 0,
    avgToInReview: 0,
    avgToApproval: 0,
    totalProcessed: 0,
    pendingCount: 0,
    inReviewCount: 0,
    approvedCount: 0,
    deniedCount: 0,
    oldestPendingDays: 0,
    processedThisMonth: 0,
    processedLastMonth: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();

    const channel = supabase
      .channel('claims-processing-stats')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'claims' }, () => fetchStats())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'claim_status_history' }, () => fetchStats())
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchStats = async () => {
    try {
      // Fetch all claims
      const { data: claims, error: claimsError } = await supabase
        .from("claims")
        .select("id, status, created_at, updated_at");

      if (claimsError) throw claimsError;

      // Fetch status history for processing time calculations
      const { data: statusHistory, error: historyError } = await supabase
        .from("claim_status_history")
        .select("claim_id, old_status, new_status, created_at")
        .order("created_at", { ascending: true });

      if (historyError) throw historyError;

      const now = new Date();
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

      // Count by status
      const pendingCount = claims?.filter(c => c.status === "pending").length || 0;
      const inReviewCount = claims?.filter(c => c.status === "in_review").length || 0;
      const approvedCount = claims?.filter(c => c.status === "approved").length || 0;
      const deniedCount = claims?.filter(c => c.status === "denied").length || 0;

      // Calculate oldest pending claim
      const pendingClaims = claims?.filter(c => c.status === "pending") || [];
      let oldestPendingDays = 0;
      if (pendingClaims.length > 0) {
        const oldest = pendingClaims.reduce((prev, curr) => 
          new Date(prev.created_at) < new Date(curr.created_at) ? prev : curr
        );
        oldestPendingDays = differenceInDays(now, new Date(oldest.created_at));
      }

      // Calculate average time to approval/denial
      const processedClaims = claims?.filter(c => c.status === "approved" || c.status === "denied") || [];
      let totalProcessingTime = 0;
      let totalToInReview = 0;
      let inReviewTransitions = 0;
      let totalToApproval = 0;
      let approvalTransitions = 0;

      for (const claim of processedClaims) {
        const claimHistory = statusHistory?.filter(h => h.claim_id === claim.id) || [];
        
        // Time from creation to final status
        const processingDays = differenceInDays(new Date(claim.updated_at), new Date(claim.created_at));
        totalProcessingTime += processingDays;

        // Time to in_review
        const toInReview = claimHistory.find(h => h.new_status === "in_review");
        if (toInReview) {
          totalToInReview += differenceInDays(new Date(toInReview.created_at), new Date(claim.created_at));
          inReviewTransitions++;
        }

        // Time to approval
        const toApproval = claimHistory.find(h => h.new_status === "approved");
        if (toApproval) {
          totalToApproval += differenceInDays(new Date(toApproval.created_at), new Date(claim.created_at));
          approvalTransitions++;
        }
      }

      // Monthly processed counts
      const processedThisMonth = statusHistory?.filter(h => {
        const date = new Date(h.created_at);
        return (h.new_status === "approved" || h.new_status === "denied") && date >= thisMonthStart;
      }).length || 0;

      const processedLastMonth = statusHistory?.filter(h => {
        const date = new Date(h.created_at);
        return (h.new_status === "approved" || h.new_status === "denied") && 
               date >= lastMonthStart && date <= lastMonthEnd;
      }).length || 0;

      setStats({
        avgProcessingDays: processedClaims.length > 0 ? Math.round(totalProcessingTime / processedClaims.length) : 0,
        avgToInReview: inReviewTransitions > 0 ? Math.round(totalToInReview / inReviewTransitions) : 0,
        avgToApproval: approvalTransitions > 0 ? Math.round(totalToApproval / approvalTransitions) : 0,
        totalProcessed: processedClaims.length,
        pendingCount,
        inReviewCount,
        approvedCount,
        deniedCount,
        oldestPendingDays,
        processedThisMonth,
        processedLastMonth,
      });
    } catch (error) {
      console.error("Error fetching claims processing stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDays = (days: number) => {
    if (days === 0) return "< 1 day";
    if (days === 1) return "1 day";
    return `${days} days`;
  };

  const getProcessingIndicator = (days: number) => {
    if (days <= 3) return { color: "text-green-500", label: "Fast" };
    if (days <= 7) return { color: "text-yellow-500", label: "Normal" };
    return { color: "text-red-500", label: "Slow" };
  };

  const getTrendIndicator = () => {
    if (stats.processedThisMonth > stats.processedLastMonth) {
      return { icon: TrendingUp, color: "text-green-500", positive: true };
    } else if (stats.processedThisMonth < stats.processedLastMonth) {
      return { icon: TrendingDown, color: "text-red-500", positive: false };
    }
    return { icon: Minus, color: "text-muted-foreground", positive: null };
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Timer className="h-5 w-5" />
            Claims Processing
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const processingIndicator = getProcessingIndicator(stats.avgProcessingDays);
  const trend = getTrendIndicator();
  const TrendIcon = trend.icon;
  const totalClaims = stats.pendingCount + stats.inReviewCount + stats.approvedCount + stats.deniedCount;
  const approvalRate = totalClaims > 0 ? Math.round((stats.approvedCount / totalClaims) * 100) : 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Timer className="h-5 w-5" />
          Claims Processing
        </CardTitle>
        <CardDescription>Processing times and claim status analytics</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Average Processing Time */}
        <div className="p-4 rounded-lg bg-muted/50 text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Avg. Processing Time</span>
          </div>
          <p className="text-3xl font-bold">{formatDays(stats.avgProcessingDays)}</p>
          <Badge variant="outline" className={processingIndicator.color}>
            {processingIndicator.label}
          </Badge>
        </div>

        {/* Processing Timeline */}
        <div className="space-y-2">
          <p className="text-sm font-medium">Average Timeline</p>
          <div className="relative">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
              <span>Submitted</span>
              <span>In Review</span>
              <span>Approved</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="flex-1 h-2 bg-yellow-200 dark:bg-yellow-900 rounded-l" />
              <div className="flex-1 h-2 bg-blue-200 dark:bg-blue-900" />
              <div className="flex-1 h-2 bg-green-200 dark:bg-green-900 rounded-r" />
            </div>
            <div className="flex items-center justify-between text-xs mt-1">
              <span>0d</span>
              <span>{formatDays(stats.avgToInReview)}</span>
              <span>{formatDays(stats.avgToApproval)}</span>
            </div>
          </div>
        </div>

        {/* Status Counts */}
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center gap-2 p-2 rounded border">
            <Clock className="h-4 w-4 text-yellow-500" />
            <div>
              <p className="text-lg font-bold">{stats.pendingCount}</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 rounded border">
            <AlertCircle className="h-4 w-4 text-blue-500" />
            <div>
              <p className="text-lg font-bold">{stats.inReviewCount}</p>
              <p className="text-xs text-muted-foreground">In Review</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 rounded border">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <div>
              <p className="text-lg font-bold">{stats.approvedCount}</p>
              <p className="text-xs text-muted-foreground">Approved</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 rounded border">
            <FileText className="h-4 w-4 text-red-500" />
            <div>
              <p className="text-lg font-bold">{stats.deniedCount}</p>
              <p className="text-xs text-muted-foreground">Denied</p>
            </div>
          </div>
        </div>

        {/* Additional Metrics */}
        <div className="pt-3 border-t space-y-3">
          <div>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-muted-foreground">Approval Rate</span>
              <span className="font-medium">{approvalRate}%</span>
            </div>
            <Progress value={approvalRate} className="h-2" />
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Processed This Month</span>
            <div className="flex items-center gap-2">
              <span className="font-medium">{stats.processedThisMonth}</span>
              <TrendIcon className={`h-4 w-4 ${trend.color}`} />
            </div>
          </div>

          {stats.oldestPendingDays > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Oldest Pending</span>
              <Badge variant={stats.oldestPendingDays > 7 ? "destructive" : "outline"}>
                {formatDays(stats.oldestPendingDays)}
              </Badge>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
